import React, {
  Fragment, useEffect, useState, memo, useMemo, useCallback,
} from 'react'
import classNames from 'classnames'
import { Ace, Search } from '../../components'
import { useQuery } from 'react-query'
import axios from 'axios'
import { produce } from 'immer'
import { FixedSizeList as List, areEqual } from 'react-window'

import { ReactComponent as Spinner } from '../../images/icons/spinner.svg'
import AutoSizer from 'react-virtualized-auto-sizer'
import { Modal } from 'antd'
import { toast } from 'react-toastify'

const Row = memo(({ data, index, style }) => {
  const { fields, onSelect, onChangeField } = data
  const item = fields[index]
  const mRow = useMemo(() => {
    return (
      <div className={classNames('row', { 'selectMap': item.flag === '1' })}
           style={style}>
        <div className={'col-4 cursorPointer'} onClick={() => onSelect(index)}>
          <label
            className={'d-block my-3 cursorPointer'}>{item.oldContent}</label>
        </div>
        <div className={'col'}>
          <div className={classNames(
            'knackly-plain rounded-0 py-2 form-control TemplateWrapper my-1')}>
            <Ace mode={'kexpr'}
                 value={item.newContent}
                 onChange={(_v) => onChangeField(item.oldContent, _v)}
                 className={'border-0'}
                 maxLines={1}
                 isExpression={true}/>
          </div>
        </div>
      </div>)
  }, [item.flag, item.oldContent, item.newContent])
  return mRow
}, areEqual)

const MapFields = () => {
  const [fields, setFields] = useState([])
  const [searchByOld, setSearchByOld] = useState('')
  const [searchByNew, setSearchByNew] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  const [inProgress, setProgress] = useState(false)
  const [isModal, setModal] = useState(false)
  const [newContent, setNewContent] = useState('')
  const [replaceValue, setReplaceValue] = useState('')

  const {
    data, isLoading: isLoadingTemplates, refetch,
  } = useQuery('/api/migration/map_fields', async () => {
    const res = await axios.get('/api/migration/map_fields')
    if (typeof res.data === 'string' && res.data.startsWith('ERROR:')) {
      // an error occurred when trying to process the Component CSV to suggest fields...
      // the component CSV has some problem and needs to be modified before it can be processed.
      // display to user (todo: where?)
      toast.error(
        <div>
          {res.data}
        </div>, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 5000,
        })
    } else {
      return res.data
    }
  }, {
    retry: false,
  })

  useEffect(() => {
    if (data) {
      setFields(data.map(item => {
        item.searchByNew = item.newContent
        return item
      }))
    }
  }, [data])

  const onSave = async () => {
    setProgress(true)
    try {
      await axios.post('/api/migration/map_fields',
        fields.filter(_t => _t.isUpdate).
          map(({ oldContent, newContent, flag }) => {
            return ({
              oldContent, newContent, flag,
            })
          }))
      await refetch()
      setHasChanges(false)
    } catch (e) {
      console.error(e)
    } finally {
      setProgress(false)
    }
  }
  const onCancel = () => {
    setNewContent('')
    setReplaceValue('')
    setModal(false)
  }

  const onSubmit = () => {
    if (!newContent || !replaceValue) {
      return
    }
    setFields(fields.map(_f => {
      if (_f.newContent === newContent) {
        _f.newContent = replaceValue
        _f.isUpdate = true
      }
      return _f
    }))
    setHasChanges(true)
    onCancel()
  }

  const onChangeField = useCallback((_old, value) => {
    setFields(produce(draft => {
      const index = draft.findIndex(_f => _f.oldContent === _old)
      if (index !== -1) {
        draft[index].newContent = value
        draft[index].isUpdate = true
      }
    }))
    setHasChanges(true)
  }, [])

  const onSelect = useCallback((index) => {
    setFields(produce(draft => {
      draft[index].flag = draft[index].flag === '0' ? '1' : '0'
      draft[index].isUpdate = true
    }))
    setHasChanges(true)
  }, [])

  if (isLoadingTemplates) {
    return <div className={'knackly-plain py-2 ml-0'}>Loading...</div>
  }

  let items = fields
  if (searchByOld) {
    items = items.filter(
      item => item.oldContent.toLowerCase().includes(searchByOld.toLowerCase()))
  }
  if (searchByNew) {
    items = items.filter(
      item => item.newContent.toLowerCase().
          includes(searchByNew.toLowerCase()) ||
        item.searchByNew.toLowerCase().includes(searchByNew.toLowerCase()))
  }
  return (<Fragment>
    <div className={'row knackly-plain py-2 ml-0'}>
      <div className={'col-4'}>
        <label>HotDocs Fields</label>
      </div>
      <div className={'col-4'}>
        <label>New Fields</label>
      </div>
      <div className={'col-4'}>
      </div>
      <div className={'col-4'}>
        <Search onSubmit={setSearchByOld}/>
      </div>
      <div className={'col-4'}>
        <Search onSubmit={setSearchByNew}/>
      </div>
      <div className={'col-4'}>
        <button
          style={{ width: 120 }}
          className={'btn btn-secondary btn-theme mb-2'}
          onClick={() => setModal(true)}
        >Replace
        </button>
      </div>

      <div className={'col-12'}>
        <div style={{ height: '60vh' }}>
          <AutoSizer>
            {({
              height, width,
            }) => (<List
              height={height}
              itemCount={items.length}
              itemData={{ fields: items, onSelect, onChangeField }}
              itemSize={49}
              width={width + 28}
            >
              {Row}
            </List>)}
          </AutoSizer>
        </div>
      </div>
      {hasChanges && (<div className={'col-3'}>
        <button
          className={'btn btn-secondary btn-block p-1 btn-theme mt-2 mb-0'}
          onClick={onSave}
          disabled={inProgress}
        >{inProgress ? <Spinner className={'rot'} style={{
          width: 30, top: -4, position: 'relative',
        }}/> : 'Save Changes'}
        </button>
      </div>)}
    </div>

    <Modal title={'Replace New Merge Fields'}
           visible={isModal}
           onCancel={onCancel}
           closable={false}
           width={410}
           footer={[
             <button key="submit" className={'btn btn-secondary dart-btn'}
                     style={{ width: 125 }}
                     disabled={!newContent || !replaceValue}
                     onClick={onSubmit}>
               Save
             </button>, <button key="back"
                                className={'btn btn-outline-secondary dart-btn-outline'}
                                style={{ width: 125 }}
                                onClick={onCancel}>Cancel</button>]}
    >
      <form onSubmit={onSubmit}>
        <label>New Merge Field:</label>
        <div className={classNames(
          'knackly-plain rounded-0 py-2 form-control TemplateWrapper my-1')}>
          <Ace mode={'kexpr'}
               value={newContent}
               onChange={setNewContent}
               className={'border-0'}
               maxLines={1}
               isExpression={true}/>
        </div>
        <label>Replace Value:</label>
        <div className={classNames(
          'knackly-plain rounded-0 py-2 form-control TemplateWrapper my-1')}>
          <Ace mode={'kexpr'}
               value={replaceValue}
               onChange={setReplaceValue}
               className={'border-0'}
               maxLines={1}
               isExpression={true}/>
        </div>
      </form>
    </Modal>
  </Fragment>)
}

export default MapFields
